import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Provider } from "react-redux";
import { Toaster } from "react-hot-toast";
import { store } from "./store";
import ProtectedRoute from "./utils/ProtectedRoute";
import SubscriptionModal from "./components/SubscriptionModal";
import RewardModal from "./components/common/RewardModal";
import ConfirmationModal from "./components/common/ConfirmationModal";
import { useSelector, useDispatch } from "react-redux";
import { closeModal, hideConfirmation } from "./features/ui/uiSlice";
import {
  CrossmintProvider,
  CrossmintAuthProvider,
  CrossmintWalletProvider,
} from "@crossmint/client-sdk-react-ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./config/queryClient";
import path from "node:path";
import { APP_ROUTES } from "@/constants/app";

const DefaultRedirect = () => {
  const { onboardingCompleted } = useSelector((state) => state.auth);

  if (onboardingCompleted) {
    return <Navigate to="/home" replace />;
  } else {
    return <Navigate to="/home" replace />;
  }
};

const NotFoundRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const ref = params.get("ref");
  const loginParams = new URLSearchParams();
  if (ref) loginParams.set("ref", ref);
  const loginUrl = loginParams.toString()
    ? `/login?${loginParams.toString()}`
    : "/login";

  return <Navigate to={loginUrl} replace />;
};

const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const EmailVerificationSentPage = React.lazy(
  () => import("./pages/EmailVerificationSentPage"),
);
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const HomePage = React.lazy(() => import("./pages/HomePage"));
const AdminPage = React.lazy(() => import("./pages/admin"));
const AdminUserPage = React.lazy(() => import("./pages/admin/user"));
const AdminReferralPage = React.lazy(() => import("./pages/admin/referral"));
const OnboardingPage = React.lazy(() => import("./pages/OnboardingPage"));
const TransactionsPage = React.lazy(() => import("./pages/TransactionsPage"));
const GoalsPage = React.lazy(() => import("./pages/GoalsPage"));
const BudgetsPage = React.lazy(() => import("./pages/BudgetsPage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const AICoachPage = React.lazy(() => import("./pages/AICoachPage"));
const TokensPage = React.lazy(() => import("./pages/TokensPage"));
const HelpPage = React.lazy(() => import("./pages/HelpPage"));
const OAuthCallbackPage = React.lazy(() => import("./pages/OAuthCallbackPage"));
const SubscriptionSuccessPage = React.lazy(
  () => import("./pages/SubscriptionSuccessPage"),
);
const EmailVerificationPage = React.lazy(
  () => import("./pages/EmailVerificationPage"),
);
const ForgotPasswordPage = React.lazy(
  () => import("./pages/ForgotPasswordPage"),
);
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const InvitePage = React.lazy(() => import("./pages/InvitePage"));
const DepositPage = React.lazy(() => import("./pages/DepositPage"));
const SelectNetworkPage = React.lazy(() => import("./pages/SelectNetwork"));
const TransactionHistoryPage = React.lazy(
  () => import("./pages/TransactionHistory"),
);
const RefRulesPage = React.lazy(() => import("./pages/RefRule"));
const RewardDetailsPage = React.lazy(() => import("./pages/RewardDetails"));
const RefUsersPage = React.lazy(() => import("./pages/RefUsers"));
const VerifyPage = React.lazy(() => import("./pages/VeryfyPage"));
const VerificationCallbackPage = React.lazy(() => import("./pages/VerifyPage"));

const CardPage = React.lazy(() => import("./pages/CardPage"));
const MyCardsPage = React.lazy(() => import("./pages/MyCardsPage"));
const CardApplicationPage = React.lazy(
  () => import("./pages/CardApplicationPage"),
);
const CardTransactionDetailPage = React.lazy(
  () => import("./pages/CardTransactionDetailPage"),
);
const StatementListPage = React.lazy(() => import("./pages/StatementListPage"));
const StatementPage = React.lazy(() => import("./pages/StatementPage"));
const StatementHistoryPage = React.lazy(
  () => import("./pages/StatementHistoryPage"),
);
const NotiPage = React.lazy(() => import("./pages/NotiPage"));
const NotiDetailPage = React.lazy(() => import("./pages/NotiDetailPage"));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const ApplyCardPage = React.lazy(() => import("./pages/ApplyCard"));
const WithdrawPage = React.lazy(() => import("./pages/WithdrawPage"));
const publicRoutes = [
  { path: "/login", Component: LoginPage },
  { path: "/admin/login", Component: LoginPage },
  { path: "/register", Component: RegisterPage },
  {
    path: "/email-verification-sent",
    Component: EmailVerificationSentPage,
  },
  { path: "/oauth-callback", Component: OAuthCallbackPage },
  { path: "/verify-email", Component: EmailVerificationPage },
  { path: "/forgot-password", Component: ForgotPasswordPage },
  { path: "/reset-password", Component: ResetPasswordPage },
  {
    path: "/subscription/success",
    Component: SubscriptionSuccessPage,
  },
];

const protectedRoutes = [
  // Home / dashboard style views
  {
    path: "/home",
    Component: HomePage,
    requireOnboarding: false,
  },
  {
    path: "/verification/callback",
    Component: VerificationCallbackPage,
    requireOnboarding: false,
  },
  {
    path: APP_ROUTES.ADMIN,
    Component: AdminPage,
    requireOnboarding: false,
    requireAdmin: true,
  },
  {
    path: APP_ROUTES.ADMIN_USER,
    Component: AdminUserPage,
    requireOnboarding: false,
    requireAdmin: true,
  },
  {
    path: APP_ROUTES.ADMIN_REFERRAL,
    Component: AdminReferralPage,
    requireOnboarding: false,
    requireAdmin: true,
  },
  {
    path: "/onboarding",
    Component: OnboardingPage,
    requireOnboarding: false,
  },
  {
    path: "/dashboard",
    Component: DashboardPage,
    requireOnboarding: false,
  },

  // Financial features (onboarding via modal where needed)
  { path: "/card/apply", Component: ApplyCardPage, requireOnboarding: false },
  {
    path: "/card/transactions/:transactionId",
    Component: CardTransactionDetailPage,
    requireOnboarding: false,
  },
  {
    path: "/card/transactions",
    Component: CardApplicationPage,
    requireOnboarding: false,
  },
  {
    path: "/card/statement-list",
    Component: StatementListPage,
    requireOnboarding: false,
  },
  {
    path: "/card/statement",
    Component: StatementPage,
    requireOnboarding: false,
  },
  {
    path: "/card/statement-history",
    Component: StatementHistoryPage,
    requireOnboarding: false,
  },
  {
    path: "/card/my-cards",
    Component: MyCardsPage,
    requireOnboarding: false,
  },
  {
    path: "/profile",
    Component: ProfilePage,
    requireOnboarding: false,
  },
  {
    path: "/notification",
    Component: NotiPage,
    requireOnboarding: false,
  },
  {
    path: "/notification/:id",
    Component: NotiDetailPage,
    requireOnboarding: false,
  },
  {
    path: "/card",
    Component: CardPage,
    requireOnboarding: false,
  },
  {
    path: "/verify",
    Component: VerifyPage,
    requireOnboarding: false,
  },
  {
    path: "/reward-details",
    Component: RewardDetailsPage,
    requireOnboarding: false,
  },
  {
    path: "/ref-rules",
    Component: RefRulesPage,
    requireOnboarding: false,
  },
  {
    path: "/ref-users",
    Component: RefUsersPage,
    requireOnboarding: false,
  },
  {
    path: "/transaction-history",
    Component: TransactionHistoryPage,
    requireOnboarding: false,
  },
  {
    path: "/select-network",
    Component: SelectNetworkPage,
    requireOnboarding: false,
  },
  {
    path: "/deposit",
    Component: DepositPage,
    requireOnboarding: false,
  },
  {
    path: "/withdraw",
    Component: WithdrawPage,
    requireOnboarding: false,
  },
  {
    path: "/invite",
    Component: InvitePage,
    requireOnboarding: false,
  },
  {
    path: "/transactions",
    Component: TransactionsPage,
    requireOnboarding: false,
    onboardingMode: "modal",
  },
  {
    path: "/budget",
    Component: GoalsPage,
    requireOnboarding: false,
    onboardingMode: "modal",
  },
  {
    path: "/budgets",
    Component: BudgetsPage,
    requireOnboarding: false,
    onboardingMode: "modal",
  },

  // AI coach & account related
  {
    path: "/ai-coach",
    Component: AICoachPage,
    requireOnboarding: false,
  },
  {
    path: "/tokens",
    Component: TokensPage,
    requireOnboarding: false,
  },
  {
    path: "/settings",
    Component: SettingsPage,
    requireOnboarding: false,
  },
  {
    path: "/help",
    Component: HelpPage,
    requireOnboarding: false,
  },
];

const AppContent = () => {
  const dispatch = useDispatch();
  const { modals, confirmationData } = useSelector((state) => state.ui);

  const handleSelectPlan = (plan) => {
    dispatch(closeModal("subscription"));
  };

  const handleConfirmationConfirm = () => {
    if (confirmationData?.onConfirm) {
      confirmationData.onConfirm();
    }
    dispatch(hideConfirmation());
  };

  const handleConfirmationClose = () => {
    dispatch(hideConfirmation());
  };

  return (
    <>
      <Routes>
        {/* Public routes */}
        {publicRoutes.map(({ path, Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}

        {/* Protected routes */}
        {protectedRoutes.map(
          ({
            path,
            Component,
            requireOnboarding,
            onboardingMode,
            requireAdmin,
          }) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute
                  requireOnboarding={requireOnboarding}
                  onboardingMode={onboardingMode}
                  requireAdmin={requireAdmin}
                >
                  <Component />
                </ProtectedRoute>
              }
            />
          ),
        )}

        {/* Default redirects */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DefaultRedirect />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>

      {/* Subscription Modal */}
      {/* <SubscriptionModal
        isOpen={modals.subscription}
        onClose={() => dispatch(closeModal("subscription"))}
        onSelectPlan={handleSelectPlan}
      /> */}
      <RewardModal />
      <ConfirmationModal
        isOpen={modals.confirmation}
        onClose={handleConfirmationClose}
        onConfirm={handleConfirmationConfirm}
        title={confirmationData?.title}
        message={confirmationData?.message}
        confirmText={confirmationData?.confirmText}
        cancelText={confirmationData?.cancelText}
        variant={confirmationData?.variant}
        icon={confirmationData?.icon}
      />
    </>
  );
};

const clientApiKey = import.meta.env.VITE_CROSSMINT_CLIENT_ID;

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <CrossmintProvider apiKey={clientApiKey}>
        <CrossmintAuthProvider
          loginMethods={["google", "email", "twitter"]}
          authModalTitle=""
          appearance={{
            spacingUnit: "8px",
            borderRadius: "12px",
            colors: {
              inputBackground: "#fff",
              buttonBackground: "#E5ECFF",
              backgroundSecondary: "#bbd1fa",
              border: "#E3E8EF",
              background: "#fff",
              textPrimary: "#18181B",
              textSecondary: "#18181B",
              textLink: "#1400cb",
              danger: "#ff3333",
              accent: "#497AFF",
            },
          }}
        >
          <CrossmintWalletProvider
            createOnLogin={{
              chain: "base-sepolia",
              signer: {
                type: "email",
              },
            }}
          >
            <Provider store={store}>
              <Router>
                <div className="App">
                  <Suspense
                    fallback={
                      <div className="flex min-h-screen items-center justify-center bg-gray-50">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                      </div>
                    }
                  >
                    <AppContent />
                  </Suspense>

                  {/* Toast notifications */}
                  <Toaster
                    position="top-right"
                    toastOptions={{
                      duration: 3000,
                      style: {
                        background: "#1f2937",
                        color: "#fff",
                        fontSize: "14px",
                        borderRadius: "12px",
                      },
                    }}
                  />
                </div>
              </Router>
            </Provider>
          </CrossmintWalletProvider>
        </CrossmintAuthProvider>
      </CrossmintProvider>
    </QueryClientProvider>
  );
}

export default App;
